import React, { useState, useEffect, useRef } from 'react';

const WhatsAppButton = () => {
  const [isHovering, setIsHovering] = useState(false);
  const [showTypingIndicator, setShowTypingIndicator] = useState(false);
  const [messageStep, setMessageStep] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const typingIntervalRef = useRef(null);
  const messageTimeoutRef = useRef(null);
  
  // TODO: Replace with your actual WhatsApp business number (without + or spaces)
  const phoneNumber = '971563252389';
  
  // Interactive message flow
  const messageFlow = [
    "Hi there! 👋",
    "I'd like to know about your interior design services",
    "Could you share some portfolio examples?",
    "What are your pricing packages?"
  ];

  const defaultMessage = messageFlow[messageStep];
  const encodedMessage = encodeURIComponent(defaultMessage);
  const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => {
      window.removeEventListener('resize', checkMobile);
      cleanupIntervals();
    };
  }, []);

  // Clean up intervals on unmount / reset
  const cleanupIntervals = () => {
    if (typingIntervalRef.current) {
      clearInterval(typingIntervalRef.current);
      typingIntervalRef.current = null;
    }
    if (messageTimeoutRef.current) {
      clearTimeout(messageTimeoutRef.current);
      messageTimeoutRef.current = null;
    }
  };

  // Handle hover to show AI-like typing flow
  const handleMouseEnter = () => {
    if (!isMobile) {
      setIsHovering(true);
      if (!typingIntervalRef.current) {
        startTypingFlow();
      }
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile) {
      setIsHovering(false);
      cleanupIntervals();
      setShowTypingIndicator(false);
      setIsTyping(false);
      setMessageStep(0);
    }
  };

  const startTypingFlow = () => {
    cleanupIntervals(); // Clear any existing intervals

    setShowTypingIndicator(true);
    setIsTyping(true);

    let step = 0;
    setMessageStep(step);

    typingIntervalRef.current = setInterval(() => {
      // cycle through messages to feel AI-like
      step = (step + 1) % messageFlow.length;
      setMessageStep(step);

      // Show typing indicator briefly before each message
      setShowTypingIndicator(true);
      setIsTyping(true);

      messageTimeoutRef.current = setTimeout(() => {
        setShowTypingIndicator(false);
        setIsTyping(false);
      }, 700);
    }, 2200);
  };

  // Handle click with loading state
  const handleClick = (e) => {
    if (!isMobile) return; // Let default link behavior on desktop
    
    e.preventDefault();
    setIsLoading(true);
    
    // Simulate loading for better UX
    setTimeout(() => {
      setIsLoading(false);
      window.open(whatsappUrl, '_blank');
    }, 600);
  };

  // Handle touch start on mobile: expand and start AI-like typing immediately
  const handleTouchStart = () => {
    if (isMobile) {
      setIsHovering(true);
      if (!typingIntervalRef.current) {
        startTypingFlow();
      }
    }
  };

  // Handle touch end on mobile: collapse after a short delay (if not navigating)
  const handleTouchEnd = () => {
    if (isMobile) {
      setTimeout(() => {
        if (!isLoading) {
          setIsHovering(false);
          cleanupIntervals();
          setShowTypingIndicator(false);
          setIsTyping(false);
        }
      }, 1500);
    }
  };

  return (
    <>
      <a
        href={whatsappUrl}
        className={`whatsapp-widget ${isHovering ? 'expanded' : ''}`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat with us on WhatsApp"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onClick={(e) => {
          if (isMobile) {
            handleClick(e);
          }
        }}
      >
        <div className={`whatsapp-icon-wrapper ${isLoading ? 'loading' : ''}`}>
          <i className="fab fa-whatsapp"></i>
          <span className="whatsapp-pulse" />
          
          {/* Loading spinner when clicked on mobile */}
          {isLoading && (
            <div className="loading-spinner">
              <div className="spinner"></div>
            </div>
          )}
          
          {/* Typing indicator */}
          {showTypingIndicator && !isLoading && (
            <div className="typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </div>
          )}
        </div>
        
        {/* Text content - only show on desktop hover or mobile touch */}
        <div className={`whatsapp-text ${isHovering ? 'show' : ''}`}>
          <span className="whatsapp-title">Chat on WhatsApp</span>
          <div className="message-preview">
            <span className={`message-text ${isTyping ? 'typing' : ''}`}>
              {messageFlow[messageStep]}
            </span>
            {showTypingIndicator && !isLoading && (
              <div className="typing-indicator-small">
                <span></span>
                <span></span>
                <span></span>
              </div>
            )}
          </div>
          <span className="whatsapp-subtitle">
            {isLoading ? 'Opening WhatsApp...' : 
             isHovering ? 'Click to send message' : 'Typically replies in minutes'}
          </span>
        </div>
      </a>

      {/* Inline styles */}
      <style>{`
        .whatsapp-widget {
          position: fixed;
          right: 20px;
          bottom: 90px;
          display: inline-flex;
          align-items: center;
          gap: 0;
          padding: 12px;
          border-radius: 999px;
          background: linear-gradient(135deg, rgba(37, 211, 101, 0.4), rgba(30, 190, 91, 0.65));
          color: #ffffff;
          text-decoration: none;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.25);
          z-index: 1050;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          cursor: pointer;
          overflow: hidden;
          width: 64px;
          height: 64px;
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .whatsapp-widget.expanded {
          width: 280px;
        }

        .whatsapp-widget:hover {
          transform: translateY(-2px) scale(1.02);
          box-shadow: 0 20px 40px rgba(37, 211, 102, 0.3);
          background: linear-gradient(135deg, rgba(37, 211, 101, 0.6), rgba(30, 190, 91, 0.8));
        }

        .whatsapp-icon-wrapper {
          position: relative;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background-color: #25D366;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          overflow: visible;
          flex-shrink: 0;
          transition: all 0.3s ease;
        }

        .whatsapp-icon-wrapper i {
          position: relative;
          z-index: 2;
          transition: transform 0.3s ease;
          color: white;
        }

        .whatsapp-widget:hover .whatsapp-icon-wrapper i {
          transform: scale(1.1);
        }

        .whatsapp-pulse {
          position: absolute;
          inset: 0;
          border-radius: 50%;
          background-color: rgba(37, 211, 102, 0.35);
          z-index: 1;
          animation: whatsappPulse 1.8s ease-out infinite;
        }

        .whatsapp-text {
          opacity: 0;
          width: 0;
          overflow: hidden;
          padding-left: 0;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          white-space: nowrap;
          color: #ffffff;
        }

        .whatsapp-text.show {
          opacity: 1;
          width: 200px;
          padding-left: 12px;
          overflow: visible;
        }

        .whatsapp-title {
          font-size: 0.85rem;
          font-weight: 600;
          letter-spacing: 0.01em;
          margin-bottom: 2px;
          opacity: 0.95;
          color: #ffffff;
        }

        .message-preview {
          font-size: 0.75rem;
          opacity: 0.95;
          display: flex;
          align-items: center;
          gap: 4px;
          margin-bottom: 2px;
          overflow: hidden;
          color: #ffffff;
        }

        .message-text {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          color: #ffffff;
        }

        .message-text.typing {
          opacity: 0.8;
        }

        .whatsapp-subtitle {
          font-size: 0.7rem;
          opacity: 0.85;
          font-weight: 400;
          color: rgba(255, 255, 255, 0.9);
        }

        /* Loading spinner */
        .loading-spinner {
          position: absolute;
          right: -4px;
          bottom: -4px;
          width: 18px;
          height: 18px;
          background: #075E54;
          border-radius: 999px;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 3;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
        }

        .spinner {
          width: 20px;
          height: 20px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-radius: 50%;
          border-top-color: #ffffff;
          animation: spin 0.8s linear infinite;
        }

        /* Typing indicator */
        .typing-indicator {
          position: absolute;
          top: -8px;
          right: -8px;
          display: flex;
          gap: 2px;
          padding: 4px 6px;
          background: #075E54;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.2);
          z-index: 4;
        }

        .typing-indicator span {
          width: 4px;
          height: 4px;
          border-radius: 50%;
          background: #ffffff;
          animation: typing 1.4s infinite ease-in-out;
        }

        .typing-indicator span:nth-child(1) { animation-delay: -0.32s; }
        .typing-indicator span:nth-child(2) { animation-delay: -0.16s; }

        .typing-indicator-small {
          display: flex;
          gap: 2px;
          align-items: center;
        }

        .typing-indicator-small span {
          width: 3px;
          height: 3px;
          border-radius: 50%;
          background: #ffffff;
          opacity: 0.8;
          animation: typing 1.4s infinite ease-in-out;
        }

        @keyframes typing {
          0%, 80%, 100% { 
            transform: translateY(0);
            opacity: 0.6;
          }
          40% { 
            transform: translateY(-4px);
            opacity: 1;
          }
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        @keyframes whatsappPulse {
          0% {
            transform: scale(1);
            opacity: 0.8;
          }
          70% {
            transform: scale(1.35);
            opacity: 0;
          }
          100% {
            transform: scale(1.35);
            opacity: 0;
          }
        }

        /* Mobile-specific styles */
        @media (max-width: 768px) {
          .whatsapp-widget {
            right: 16px;
            bottom: 80px;
            width: 64px;
            padding: 12px;
            gap: 0;
            background: linear-gradient(135deg, rgba(37, 211, 101, 0.6), rgba(30, 190, 91, 0.8));
          }

          .whatsapp-widget.expanded {
            width: 260px;
          }

          .whatsapp-widget:active {
            transform: scale(0.95);
            transition: transform 0.1s ease;
          }

          .whatsapp-icon-wrapper {
            width: 40px;
            height: 40px;
            font-size: 20px;
          }

          .whatsapp-text {
            display: none;
            opacity: 0;
            width: 0;
            padding-left: 0;
          }

          .whatsapp-text.show {
            display: block;
            opacity: 1;
            width: 200px;
            padding-left: 12px;
          }
          
          /* Make icon slightly larger on mobile for better tap target */
          .whatsapp-icon-wrapper i {
            font-size: 22px;
          }
        }

        /* Small mobile devices */
        @media (max-width: 480px) {
          .whatsapp-widget {
            right: 12px;
            bottom: 70px;
            width: 56px;
            height: 56px;
            padding: 10px;
          }

          .whatsapp-icon-wrapper {
            width: 36px;
            height: 36px;
          }
          
          .whatsapp-text.show {
            width: 180px;
          }
          
          .whatsapp-title {
            font-size: 0.8rem;
          }
          
          .message-preview {
            font-size: 0.7rem;
          }
          
          .whatsapp-subtitle {
            font-size: 0.65rem;
          }
        }

        /* Very small mobile devices */
        @media (max-width: 360px) {
          .whatsapp-widget {
            width: 52px;
            height: 52px;
          }

          .whatsapp-icon-wrapper {
            width: 32px;
            height: 32px;
          }
          
          .whatsapp-text.show {
            width: 160px;
          }
        }
      `}</style>
    </>
  );
};

export default WhatsAppButton;

